/**
 * ItemsTab — line items management tab with insert menu, drag-and-drop table,
 * stock availability, customer pricing indicator, and bundle template button.
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Purely presentational: all state and handlers are passed as props.
 */

import React, { useState as useReactState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Plus, ChevronDown, Package, Trash2, AlertCircle, CheckCircle2, Layers, List, Sparkles, Check, HelpCircle, Building2, Briefcase, Undo2, RefreshCw, Truck } from 'lucide-react';
import {
  type DocumentConfig,
  type DocumentFormState,
  type InsertMenuPlacement,
} from '../documentModalHelpers';
import {
  type Document,
  type DocumentType,
  type DocumentLineItem,
} from '@/hooks/useDocumentsWorkItems';
import { type Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import SortableLineItem, { type DocumentTaxRate, type DocumentLineItemValue } from '../SortableLineItem';
import { BulkStockAvailabilityCheck } from '@/components/inventory/StockAvailabilityCheck';
import { type StockAvailabilityCheck as StockAvailabilityCheckType } from '@/lib/inventory/stockReservationService';
import { type ExpenseAccountOption, type AllocationType, getAllowedAllocationTypes, getPreferredAllocationType } from '@/lib/finance/expenseAccountMapping';
import type { InventoryItemMatch } from '@/lib/finance/matching/aiMatcher';
import { type FinanceItem, type WorkItemInventory, mapWorkItemToFinanceItem } from '../ItemSearchModal';
import CreateItemSlideOver from '../CreateItemSlideOver';
import { type LineItemAllocation } from '@/hooks/useAllocationDimensions';
import { type SplitAllocation } from '@/components/shared/finance/SplitAllocationEditor';
import { AsyncSearchableSelect } from '@/components/shared/finance/AsyncSearchableSelect';
import { SearchableAccountSelect } from '@/components/shared/finance/SearchableAccountSelect';
import { authService } from '@/lib/authService';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

/** AI per-line categorization suggestion (full allocation chain). */
export interface AiPerLineCategorization {
  index: number;
  account_code: string;
  account_name: string;
  confidence: number;
  reasons: string[];
  allocation_type?: AllocationType;
  inventory_item_match?: InventoryItemMatch | null;
  suggested_customer_id?: string | null;
  suggested_customer_name?: string | null;
  suggested_job_id?: string | null;
  suggested_job_name?: string | null;
}

export interface ItemsTabProps {
  isActive: boolean;
  isDarkMode: boolean;
  isCompactMobileDoc: boolean;
  isExpenseDoc: boolean;
  isEditing: boolean;
  isPurchaseDocument: boolean;
  doc_type: DocumentType;
  config: DocumentConfig;
  formData: DocumentFormState;
  document: Document | null | undefined;
  items: DocumentLineItem[];
  setItems: React.Dispatch<React.SetStateAction<DocumentLineItem[]>>;
  // Insert menu
  insertButtonRef: React.RefObject<HTMLButtonElement | null>;
  openInsertMenu: InsertMenuPlacement | null;
  toggleInsertMenu: (placement: InsertMenuPlacement) => void;
  renderInsertMenu: (placement: InsertMenuPlacement) => React.ReactNode;
  // Line item operations
  canAddLineItems: boolean;
  lineItemContactLabel: string;
  hasContactSelected: boolean;
  selectedItemIndices: number[];
  setSelectedItemIndices: React.Dispatch<React.SetStateAction<number[]>>;
  handleItemChange: (index: number, field: keyof DocumentLineItem, value: DocumentLineItemValue) => void;
  handleRemoveItem: (index: number) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  // Tax rates
  taxRates: DocumentTaxRate[];
  preferredDefaultTaxRate: DocumentTaxRate | undefined;
  // Currency
  formatCurrency: (value: number | string | undefined | null) => string;
  currencySymbol: string;
  // Contact
  selectedContact: Contact | null;
  // Inline search
  inlineSearchIndex: number | null;
  setInlineSearchIndex: React.Dispatch<React.SetStateAction<number | null>>;
  handleInlineItemSelect: (item: FinanceItem, indexOverride?: number) => void;
  // AI categorization
  aiPerLineCategorization: AiPerLineCategorization[];
  /** Whole-document GL suggestion — drives the single-GL bar. */
  aiDocSuggestion?: AiPerLineCategorization | null;
  aiSuggestionsLoading: boolean;
  handleApplyAICategorization: (accountCode: string, lineIndex: number) => void;
  handleApplyFullAllocation?: (lineIndex: number, suggestion: {
    account_code: string;
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
  }) => void;
  // Single GL allocation mode (applies one GL to all line items)
  handleApplySingleGLAllocation?: (suggestion: {
    account_code: string;
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
  }) => void;
  glAllocationMode: 'single' | 'per_line';
  setGlAllocationMode: React.Dispatch<React.SetStateAction<'single' | 'per_line'>>;
  // Account & allocation
  expenseAccounts: ExpenseAccountOption[];
  handleLineItemAccountChange: (index: number, accountCode: string) => void;
  handleLineItemAllocationTypeChange: (index: number, allocType: AllocationType) => void;
  allocationDimensions: ReturnType<typeof import('@/hooks/useAllocationDimensions').useAllocationDimensions>['dimensions'];
  expandedAllocations: Set<number>;
  setExpandedAllocations: React.Dispatch<React.SetStateAction<Set<number>>>;
  getLineItemDimensions: (index: number) => LineItemAllocation[];
  getLineItemSplits: (index: number) => SplitAllocation[];
  updateLineItemAllocations: (index: number, allocations: { dimensions?: LineItemAllocation[]; splits?: SplitAllocation[] }) => void;
  // Split
  splitIdx: number | null;
  splitQty: number;
  setSplitIdx: React.Dispatch<React.SetStateAction<number | null>>;
  setSplitQty: React.Dispatch<React.SetStateAction<number>>;
  handleSplitLineItem: (idx: number, qtyToSplit: number) => void;
  // Stock availability
  showStockAvailability: boolean;
  setShowStockAvailability: React.Dispatch<React.SetStateAction<boolean>>;
  stockAvailability: Map<string, StockAvailabilityCheckType[]>;
  // Customer pricing
  customerPricingApplied: boolean;
  // Bundle
  setShowBundleSelectModal: React.Dispatch<React.SetStateAction<boolean>>;
  // Total lock mode
  totalLocked: boolean;
  hasDiscrepancy: boolean;
  discrepancy: number;
  onAddBalancingLine: () => void;
  // Document-wide tax display mode
  taxDisplayMode: 'incl' | 'excl';
  onTaxDisplayModeChange: (mode: 'incl' | 'excl') => void;
  // Undo support
  canUndo?: boolean;
  onUndo?: () => void;
  // Re-analyze from GL Allocation section: re-OCRs the first photo attachment
  // via total-vision, then re-runs AI categorization (aiMatcher + semantic intake)
  // to produce fresh GL suggestions. Disabled when there are no photo attachments
  // or when OCR is already running.
  onReanalyzeFromGL?: () => void;
  reanalyzeLoading?: boolean;
  reanalyzeDisabled?: boolean;
}

export function ItemsTab({
  isActive,
  isDarkMode,
  isCompactMobileDoc,
  isExpenseDoc,
  isEditing,
  isPurchaseDocument,
  doc_type,
  config,
  formData,
  document,
  items,
  setItems,
  insertButtonRef,
  openInsertMenu,
  toggleInsertMenu,
  renderInsertMenu,
  canAddLineItems,
  lineItemContactLabel,
  hasContactSelected,
  selectedItemIndices,
  setSelectedItemIndices,
  handleItemChange,
  handleRemoveItem,
  handleDragEnd,
  sensors,
  taxRates,
  preferredDefaultTaxRate,
  formatCurrency,
  currencySymbol,
  selectedContact,
  inlineSearchIndex,
  setInlineSearchIndex,
  handleInlineItemSelect,
  aiPerLineCategorization,
  aiDocSuggestion,
  aiSuggestionsLoading,
  handleApplyAICategorization,
  handleApplyFullAllocation,
  handleApplySingleGLAllocation,
  glAllocationMode,
  setGlAllocationMode,
  expenseAccounts,
  handleLineItemAccountChange,
  handleLineItemAllocationTypeChange,
  allocationDimensions,
  expandedAllocations,
  setExpandedAllocations,
  getLineItemDimensions,
  getLineItemSplits,
  updateLineItemAllocations,
  splitIdx,
  splitQty,
  setSplitIdx,
  setSplitQty,
  handleSplitLineItem,
  showStockAvailability,
  setShowStockAvailability,
  stockAvailability,
  customerPricingApplied,
  setShowBundleSelectModal,
  totalLocked,
  hasDiscrepancy,
  discrepancy,
  onAddBalancingLine,
  taxDisplayMode,
  onTaxDisplayModeChange,
  canUndo,
  onUndo,
  onReanalyzeFromGL,
  reanalyzeLoading,
  reanalyzeDisabled,
}: ItemsTabProps) {
  const { primaryColor } = useTheme();
  // % Mark-up and the Incl/Excl VAT toggle belong to the expense family
  // (EXP / BIL / DIN) — intake and billable-expense costing, not sales docs.
  const isDinDoc = doc_type === 'din';
  const showMarkup = isExpenseDoc && Boolean(config.fields.showMarkup);
  // Quick-create item state — opened from the inline item search dropdown.
  // `index` records which line the item is destined for; by the time the
  // slide-over resolves, click-outside may have already cleared
  // inlineSearchIndex, so the index must travel with the request.
  const [createItemRequest, setCreateItemRequest] = React.useState<{ index: number; term: string } | null>(null);
  if (!isActive) return null;

  return (
    <div data-tour-id={isExpenseDoc ? 'expense-ai-suggestions' : 'doc-modal-line-items'}>
      {/* Unified Insert Button - above line items */}
      <div className="mb-4 space-y-2.5">
        <div className={`${isCompactMobileDoc ? 'space-y-3' : 'flex items-center justify-between gap-3'}`}>
          <div className={isCompactMobileDoc ? 'grid grid-cols-1 gap-2 w-full' : 'flex items-center gap-2'}>
            {/* Unified Insert Dropdown */}
            <div className={`relative ${isCompactMobileDoc ? 'w-full' : ''}`} data-insert-menu>
              <button
                ref={insertButtonRef}
                type="button"
                onClick={() => canAddLineItems && toggleInsertMenu('top')}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                    if (canAddLineItems && !openInsertMenu) {
                      e.preventDefault();
                      toggleInsertMenu('top');
                    }
                  }
                }}
                disabled={!canAddLineItems}
                className={`flex items-center font-medium touch-manipulation ${isCompactMobileDoc ? 'w-full justify-between px-3 py-3 rounded-xl text-sm min-h-[48px]' : 'gap-2 px-3 py-1.5 rounded-lg text-xs font-medium'} ${canAddLineItems
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                title={!canAddLineItems ? `Please select ${lineItemContactLabel} first` : ''}
              >
                <Plus size={16} />
                Insert
                <ChevronDown size={14} className={`transition-transform ${openInsertMenu === 'top' ? 'rotate-180' : ''}`} />
              </button>
              {renderInsertMenu('top')}
            </div>

            {/* Import from Jobs - temporarily disabled */}

            {/* Apply Bundle Template — hidden for expense, din, and supplier_invoice
                since these document types don't use bundle templates */}
            {!['expense', 'din', 'supplier_invoice'].includes(doc_type) && (
              <button
                type="button"
                onClick={() => setShowBundleSelectModal(true)}
                disabled={!canAddLineItems}
                className={`flex items-center ${isCompactMobileDoc ? 'w-full justify-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold min-h-[48px]' : 'gap-2 px-3 py-1.5 rounded-lg text-xs font-medium'} ${!canAddLineItems
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'text-white hover:opacity-90'
                  }`}
                style={!canAddLineItems ? undefined : { backgroundColor: primaryColor }}
                title={!canAddLineItems ? `Please select ${lineItemContactLabel} first` : ''}
              >
                <Package size={16} />
                Apply Template
              </button>
            )}

            {/* Undo button — restores the previous line item state */}
            {canUndo && onUndo && (
              <button
                type="button"
                onClick={onUndo}
                className={`flex items-center gap-1.5 ${isCompactMobileDoc ? 'w-full justify-center px-3 py-3 rounded-xl text-sm font-medium min-h-[48px]' : 'px-3 py-1.5 rounded-lg text-xs font-medium'} ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                title="Undo last line item edit (Ctrl+Z)"
              >
                <Undo2 size={14} />
                Undo
              </button>
            )}
          </div>

          {selectedItemIndices.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setItems(prev => prev.filter((_, i) => !selectedItemIndices.includes(i)));
                setSelectedItemIndices([]);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${isCompactMobileDoc ? 'w-full min-h-[48px] justify-center' : ''} ${isDarkMode
                ? 'text-red-400 hover:bg-red-900/30'
                : 'text-red-600 hover:bg-red-50'
                }`}
            >
              <Trash2 size={14} />
              Delete Selected ({selectedItemIndices.length})
            </button>
          )}

          {/* Tax Display Toggle — Incl/Excl VAT — DIN only */}
          {isDinDoc && (
          <div data-tour-id="expense-tax-display-toggle" className={`inline-flex items-center rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-gray-50'} ${isCompactMobileDoc ? 'w-full min-h-[48px]' : ''}`}>
            <button
              type="button"
              onClick={() => onTaxDisplayModeChange('incl')}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-l-lg transition-colors ${isCompactMobileDoc ? 'flex-1' : ''} ${
                taxDisplayMode === 'incl'
                  ? 'bg-blue-600 text-white'
                  : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Prices include VAT — tax is extracted from the line item price"
            >
              Incl VAT
            </button>
            <button
              type="button"
              onClick={() => onTaxDisplayModeChange('excl')}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-r-lg transition-colors border-l ${isCompactMobileDoc ? 'flex-1' : ''} ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              } ${
                taxDisplayMode === 'excl'
                  ? 'bg-blue-600 text-white'
                  : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Prices exclude VAT — tax is added on top of the line item price"
            >
              Excl VAT
            </button>
          </div>
          )}
        </div>

        {!isEditing && !hasContactSelected && (
          <div className={`inline-flex max-w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs ${isDarkMode
            ? 'bg-sky-900/20 text-sky-300'
            : 'bg-sky-50 text-sky-800'
            }`}>
            <AlertCircle size={14} className="flex-shrink-0" />
            <span>
              Select {lineItemContactLabel} above to unlock item inserts and templates.
            </span>
          </div>
        )}
      </div>

      {/* GL Allocation Mode Toggle + Single GL Section (expense/din/supplier_invoice only) */}
      {isExpenseDoc && expenseAccounts.length > 0 && (
        <div data-tour-id="expense-gl-allocation" className={`mb-2 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/50'}`}>
          {/* Toggle row */}
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>GL Allocation</span>
              <div className={`flex rounded-md overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <button
                  type="button"
                  onClick={() => setGlAllocationMode('single')}
                  className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    glAllocationMode === 'single'
                      ? 'text-white'
                      : (isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-500 hover:bg-gray-100')
                  }`}
                  style={glAllocationMode === 'single' ? { backgroundColor: primaryColor } : undefined}
                  title="One GL account for all line items"
                >
                  <Layers className="h-3 w-3" />
                  Single GL
                </button>
                <button
                  type="button"
                  onClick={() => setGlAllocationMode('per_line')}
                  className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    glAllocationMode === 'per_line'
                      ? 'text-white'
                      : (isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-500 hover:bg-gray-100')
                  }`}
                  style={glAllocationMode === 'per_line' ? { backgroundColor: primaryColor } : undefined}
                  title="Each line item gets its own GL allocation"
                >
                  <List className="h-3 w-3" />
                  Per-line
                </button>
              </div>
              {/* Re-analyze button: re-runs aiMatcher categorization only —
                  whole-document suggestion in single-GL mode, per-line in
                  per-line mode. Does not re-OCR or touch supplier matching. */}
              {onReanalyzeFromGL && (
                <button
                  type="button"
                  onClick={onReanalyzeFromGL}
                  disabled={reanalyzeLoading || reanalyzeDisabled}
                  className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ${
                    reanalyzeLoading || reanalyzeDisabled
                      ? (isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-600 cursor-not-allowed' : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed')
                      : (isDarkMode ? 'border-sky-700 bg-sky-900/30 text-sky-300 hover:bg-sky-900/50' : 'border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100')
                  }`}
                  title={reanalyzeDisabled ? 'No line items to categorize' : 'Re-run AI GL categorization'}
                >
                  <RefreshCw className={`h-3 w-3 ${reanalyzeLoading ? 'animate-spin' : ''}`} />
                  {reanalyzeLoading ? 'Analyzing…' : 'Re-analyze'}
                </button>
              )}
            </div>
            {glAllocationMode === 'single' && (
              <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Applies to all {items.filter(it => !['header','comment','spacer','subtotal'].includes(it.line_type || '')).length} line items
              </span>
            )}
          </div>

          {/* Single GL allocation dropdown (only in single mode) */}
          {glAllocationMode === 'single' && (
            <SingleGLAllocationBar
              isDarkMode={isDarkMode}
              expenseAccounts={expenseAccounts}
              items={items}
              aiPerLineCategorization={aiPerLineCategorization}
              aiDocSuggestion={aiDocSuggestion ?? null}
              aiSuggestionsLoading={aiSuggestionsLoading}
              doc={document}
              onApplySingleGL={handleApplySingleGLAllocation}
            />
          )}
        </div>
      )}

      {/* Line Items Table */}
      <div className={`border rounded-lg ${isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`} data-tour-id={isExpenseDoc ? 'expense-line-items' : 'line-items-section'}>
        <div className={isCompactMobileDoc ? 'overflow-visible [&_table]:w-full [&_table]:min-w-0 [&_th]:px-1 [&_th]:py-1 [&_th]:text-[9px] [&_td]:px-1 [&_td]:py-1 [&_input]:text-[11px] [&_input]:py-0.5 [&_input]:min-w-0 [&_select]:text-[11px] [&_select]:py-0.5 [&_textarea]:text-[11px]' : 'overflow-visible'}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full">
              <thead className={`border-b ${isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-gray-50 border-gray-200'
                }`}>
                <tr>
                  <th className="px-2 py-2 w-16"></th>
                  <th className={`px-2 py-2 text-left text-xs font-semibold uppercase w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Code</th>
                  <th className={`px-2 py-2 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Description</th>
                  <th className={`px-2 py-2 text-right text-xs font-semibold uppercase w-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Qty</th>
                  <th className={`px-2 py-2 text-right text-xs font-semibold uppercase w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Price</th>
                  {showMarkup && (
                    <th className={`px-2 py-2 text-right text-xs font-semibold uppercase w-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>% Mark-up</th>
                  )}
                  <th className={`px-2 py-2 text-center text-xs font-semibold uppercase w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Tax</th>
                  <th className={`px-2 py-2 text-right text-xs font-semibold uppercase w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Total</th>
                  <th className="px-2 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody className={isDarkMode ? 'bg-gray-800/50' : ''}>
                <SortableContext
                  items={items.map((_, idx) => idx)}
                  strategy={verticalListSortingStrategy}
                >
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={showMarkup ? 9 : 8} className={`px-4 py-12 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                        <Package className={`mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} size={32} />
                        <p>No items added yet</p>
                        <p className="text-sm mt-1">{isCompactMobileDoc ? 'Tap “Insert” to choose what to add' : 'Click “Insert” to choose what to add'}</p>
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <SortableLineItem
                        key={index}
                        item={item}
                        index={index}
                        isSelected={selectedItemIndices.includes(index)}
                        formatCurrency={formatCurrency}
                        currencySymbol={currencySymbol}
                        onItemChange={handleItemChange}
                        onRemove={handleRemoveItem}
                        onToggleSelection={(idx) => {
                          setSelectedItemIndices(prev =>
                            prev.includes(idx)
                              ? prev.filter(i => i !== idx)
                              : [...prev, idx]
                          );
                        }}
                        taxRates={taxRates}
                        isDarkMode={isDarkMode}
                        preferredDefaultTaxRateId={preferredDefaultTaxRate?.id}
                        showPurchaseSourcing={isPurchaseDocument}
                        documentSupplierContactId={formData.contact_id}
                        doc_type={doc_type}
                        expenseAccounts={expenseAccounts}
                        documentId={document?.id || formData.id || ''}
                        inlineSearchOpen={inlineSearchIndex === index}
                        onInlineItemSelect={handleInlineItemSelect}
                        onInlineSearchOpen={() => setInlineSearchIndex(index)}
                        onInlineSearchClose={() => setInlineSearchIndex(null)}
                        onInlineCreateItem={(idx, term) => setCreateItemRequest({ index: idx, term })}
                        inlineSearchDocumentType={isPurchaseDocument ? 'purchase' : 'sales'}
                        inlineSearchContactId={formData.contact_id}
                        inlineSearchCurrency={formData.currency || 'ZAR'}
                        inlineSearchScopeToContact={Boolean(formData.contact_id)}
                        aiSuggestion={aiPerLineCategorization.find(s => s.index === index) ?? null}
                        aiSuggestionsLoading={aiSuggestionsLoading}
                        onApplyAICategorization={handleApplyAICategorization}
                        onApplyFullAllocation={handleApplyFullAllocation}
                        onAccountChange={handleLineItemAccountChange}
                        onAllocationTypeChange={handleLineItemAllocationTypeChange}
                        glAllocationMode={glAllocationMode}
                        splitIdx={splitIdx}
                        splitQty={splitQty}
                        onSplitIdxChange={setSplitIdx}
                        onSplitQtyChange={setSplitQty}
                        onSplitConfirm={handleSplitLineItem}
                        isBillable={(item as DocumentLineItem & { is_billable?: boolean }).is_billable ?? false}
                        billableToJobId={(item as DocumentLineItem & { billable_to_job_id?: string }).billable_to_job_id ?? ''}
                        markupPercentage={(item as DocumentLineItem & { markup_percentage?: number }).markup_percentage ?? 0}
                        showMarkup={showMarkup}
                        isPending={!['closed', 'cancelled', 'void', 'applied'].includes(String(formData.status || 'draft'))}
                        billableCustomerName={selectedContact?.company_name || selectedContact?.display_name || selectedContact?.contact_person || undefined}
                        allocationDimensions={allocationDimensions}
                        expandedAllocations={expandedAllocations}
                        onToggleExpandedAllocations={(idx) => setExpandedAllocations(prev => {
                          const next = new Set(prev);
                          if (next.has(idx)) next.delete(idx); else next.add(idx);
                          return next;
                        })}
                        getLineItemDimensions={getLineItemDimensions}
                        getLineItemSplits={getLineItemSplits}
                        onUpdateLineItemAllocations={updateLineItemAllocations}
                      />
                    ))
                  )}
                </SortableContext>
              </tbody>
              {isDinDoc && totalLocked && hasDiscrepancy && (
                <tfoot>
                  <tr className={`border-t-2 ${isDarkMode ? 'border-red-700/50 bg-red-900/20' : 'border-red-300 bg-red-50'}`}>
                    <td colSpan={showMarkup ? 7 : 6} className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                          Line items total {currencySymbol} {formatCurrency(Math.abs(discrepancy) > 0 ? (Number(formData.grand_total) || 0) - discrepancy : 0)}
                          {' '}/ Slip total {currencySymbol} {formatCurrency(Number(formData.grand_total) || 0)}
                        </span>
                      </div>
                    </td>
                    <td colSpan={showMarkup ? 2 : 2} className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={onAddBalancingLine}
                        className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                          isDarkMode
                            ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-400/40'
                            : 'bg-red-100 hover:bg-red-200 text-red-700 border border-red-300'
                        }`}
                      >
                        <Plus className="h-3 w-3" />
                        Add balancing line ({currencySymbol} {formatCurrency(discrepancy)})
                      </button>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </DndContext>
        </div>
      </div>

      {/* Stock Availability Check Display */}
      {showStockAvailability && stockAvailability.size > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Stock Availability
            </h4>
            <button
              type="button"
              onClick={() => setShowStockAvailability(false)}
              className={`text-xs ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Hide
            </button>
          </div>
          {Array.from(stockAvailability.entries()).map(([workItemId, availabilities]) => (
            <div key={workItemId} className="mb-2">
              <BulkStockAvailabilityCheck
                availabilities={availabilities}
                showDetails={false}
                isDark={isDarkMode}
                onItemSelect={(itemId) => {
                  // Focus on the item in the table
                  const itemIndex = items.findIndex(item => String(item.item_id) === itemId);
                  if (itemIndex >= 0) {
                    setSelectedItemIndices([itemIndex]);
                  }
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Customer Pricing Applied Indicator */}
      {customerPricingApplied && (
        <div className={`mt-3 p-3 rounded-lg border ${isDarkMode ? 'bg-blue-950/30 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
            <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Customer-specific pricing applied
            </span>
          </div>
        </div>
      )}

      {/* Insert button below line items - convenient for long lists */}
      {canAddLineItems && items.length > 0 && (
        <div className={`mt-3 flex flex-wrap items-center gap-2`}>
          <div className="relative" data-insert-menu>
            <button
              type="button"
              onClick={() => toggleInsertMenu('bottom')}
              className={`flex items-center gap-2 border border-dashed transition-all ${isCompactMobileDoc ? 'px-3 py-2 rounded-xl text-[11px] font-semibold' : 'px-3 py-1.5 rounded-lg text-xs font-medium'} ${isDarkMode
                ? 'border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-900/20'
                : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
                }`}
            >
              <Plus size={14} />
              Insert
              <ChevronDown size={12} className={`transition-transform ${openInsertMenu === 'bottom' ? 'rotate-180' : ''}`} />
            </button>
            {renderInsertMenu('bottom')}
          </div>
          <span className={`text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            {items.length} item{items.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Quick-create catalog item — the created item is mapped and applied
          to the originating line so its code resolves to a real catalog entry. */}
      {createItemRequest && (
        <CreateItemSlideOver
          onClose={() => setCreateItemRequest(null)}
          prefillName={createItemRequest.term}
          prefillCode={createItemRequest.term}
          currency={formData.currency || 'ZAR'}
          onCreated={(created) => {
            const row = { ...(created as WorkItemInventory) };
            const rawRow = row as Record<string, unknown>;
            // The POST returns custom_fields as a JSON string — normalize so
            // mapWorkItemToFinanceItem can read pricing fields from it.
            if (typeof rawRow.custom_fields === 'string') {
              try {
                row.custom_fields = JSON.parse(rawRow.custom_fields);
              } catch {
                row.custom_fields = null;
              }
            }
            if (!row.unit && rawRow.unit_of_measure) {
              row.unit = String(rawRow.unit_of_measure);
            }
            handleInlineItemSelect(mapWorkItemToFinanceItem(row), createItemRequest.index);
            setCreateItemRequest(null);
          }}
        />
      )}
    </div>
  );
}

export default ItemsTab;

// ============================================================================
// SingleGLAllocationBar — shows one GL dropdown for all line items (single mode)
// ============================================================================

interface SingleGLAllocationBarProps {
  isDarkMode: boolean;
  expenseAccounts: ExpenseAccountOption[];
  items: DocumentLineItem[];
  aiPerLineCategorization: AiPerLineCategorization[];
  aiDocSuggestion: AiPerLineCategorization | null;
  aiSuggestionsLoading: boolean;
  /** The parent document (for vehicle linkage fallback from custom_fields) */
  doc?: Document | null;
  onApplySingleGL?: (suggestion: {
    account_code: string;
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
    suggested_vehicle_id?: string | null;
    suggested_vehicle_name?: string | null;
  }) => void;
  onApplyAICategorization?: (accountCode: string, lineIndex: number) => void;
}

function SingleGLAllocationBar({
  isDarkMode,
  expenseAccounts,
  items,
  aiPerLineCategorization,
  aiDocSuggestion,
  aiSuggestionsLoading,
  doc,
  onApplySingleGL,
}: SingleGLAllocationBarProps) {
  const [selectedAccountCode, setSelectedAccountCode] = React.useState('');
  // Customer/job link state for job allocation in single mode
  const [selectedCustomerId, setSelectedCustomerId] = useReactState('');
  const [selectedCustomerName, setSelectedCustomerName] = useReactState('');
  const [selectedJobId, setSelectedJobId] = useReactState('');
  const [selectedJobName, setSelectedJobName] = useReactState('');
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useReactState('');
  const [selectedInventoryItemName, setSelectedInventoryItemName] = useReactState('');
  const [selectedVehicleId, setSelectedVehicleId] = useReactState('');
  const [selectedVehicleName, setSelectedVehicleName] = useReactState('');
  const [vehiclesCache, setVehiclesCache] = useReactState<Array<{ id: string; title: string; registration?: string | null }>>([]);
  const [customersCache, setCustomersCache] = useReactState<Array<{ id: string; company_name: string | null; contact_person: string | null }>>([]);
  const [jobsCache, setJobsCache] = useReactState<Array<{ id: string; item_number: string; title: string; status: string }>>([]);
  const [inventoryItemsCache, setInventoryItemsCache] = useReactState<Array<{ id: string; title: string; sku?: string | null; item_number?: string | null }>>([]);

  // Use the first line item's current GL as the default selection
  React.useEffect(() => {
    const firstProductive = items.find(it => {
      const lt = it.line_type || 'item';
      return lt !== 'header' && lt !== 'comment' && lt !== 'spacer' && lt !== 'subtotal';
    });
    if (firstProductive?.expense_account_code) {
      setSelectedAccountCode(firstProductive.expense_account_code);
    }
    // Restore customer/job from existing line items
    const firstWithCustomer = items.find(it => (it as DocumentLineItem & { linked_customer_id?: string }).linked_customer_id);
    if (firstWithCustomer) {
      const cid = (firstWithCustomer as DocumentLineItem & { linked_customer_id?: string }).linked_customer_id!;
      setSelectedCustomerId(cid);
      setSelectedCustomerName((firstWithCustomer as DocumentLineItem & { linked_customer_name?: string }).linked_customer_name || '');
    }
    const firstWithJob = items.find(it => it.linked_job_id);
    if (firstWithJob) {
      setSelectedJobId(firstWithJob.linked_job_id!);
      setSelectedJobName((firstWithJob as DocumentLineItem & { linked_job_name?: string }).linked_job_name || '');
    }
    // Restore inventory item from existing line items
    const firstWithInventory = items.find(it => (it as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id);
    if (firstWithInventory) {
      const invId = (firstWithInventory as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id!;
      setSelectedInventoryItemId(invId);
      setSelectedInventoryItemName((firstWithInventory as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name || '');
    }
    // Restore vehicle link from existing line items or document custom_fields
    const firstWithVehicle = items.find(it => (it as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id);
    if (firstWithVehicle) {
      const vid = (firstWithVehicle as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id!;
      setSelectedVehicleId(vid);
      setSelectedVehicleName((firstWithVehicle as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name || '');
    } else {
      // Fall back to the document-level vehicle linkage from SmartTask capture
      const docCf = (doc?.custom_fields as Record<string, unknown> | undefined) || {};
      const docVid = (docCf.vehicle_id as string) || null;
      const docVreg = (docCf.vehicle_registration as string) || null;
      if (docVid || docVreg) {
        setSelectedVehicleId(docVid || '');
        setSelectedVehicleName(docVreg || '');
      }
    }
  }, [items, setSelectedCustomerId, setSelectedCustomerName, setSelectedJobId, setSelectedJobName, setSelectedInventoryItemId, setSelectedInventoryItemName, setSelectedVehicleId, setSelectedVehicleName, doc]);

  // Check if all line items already share the same GL
  const productiveItems = items.filter(it => {
    const lt = it.line_type || 'item';
    return lt !== 'header' && lt !== 'comment' && lt !== 'spacer' && lt !== 'subtotal';
  });
  const allSameGL = productiveItems.length > 0 && productiveItems.every(it =>
    (it.expense_account_code || '') === (productiveItems[0].expense_account_code || '')
  );
  const currentGLCode = allSameGL ? (productiveItems[0]?.expense_account_code || '') : '';
  const currentGLAccount = expenseAccounts.find(a => a.account_code === currentGLCode);

  // Auto-apply GL account to all line items when the user selects one in
  // single mode. This ensures the final action gate passes without requiring
  // a manual "Apply" click, and preserves existing job/customer links from
  // SmartTask capture (the handleApplySingleGLAllocation function now
  // preserves existing links when the suggestion doesn't provide replacements).
  const prevAccountCode = React.useRef('');
  React.useEffect(() => {
    if (!selectedAccountCode) return;
    if (selectedAccountCode === prevAccountCode.current) return;
    prevAccountCode.current = selectedAccountCode;
    // Only auto-apply if the items don't already all have this GL
    const allHaveGL = productiveItems.length > 0 && productiveItems.every(it =>
      (it.expense_account_code || '') === selectedAccountCode
    );
    if (allHaveGL) return;
    if (!onApplySingleGL) return;
    onApplySingleGL({
      account_code: selectedAccountCode,
      // Don't pass customer/job here — let the handler preserve existing links.
      // Customer/job are applied separately when the user selects them.
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- productiveItems changes on every render; we only want to fire on account code change
  }, [selectedAccountCode]);

  // Auto-apply customer/job links when they change in single mode.
  // This ensures the gate sees the linked_job_id without a manual "Apply with links" click.
  const prevCustomerId = React.useRef('');
  const prevJobId = React.useRef('');
  React.useEffect(() => {
    if (!selectedAccountCode) return;
    if (selectedCustomerId === prevCustomerId.current && selectedJobId === prevJobId.current) return;
    prevCustomerId.current = selectedCustomerId;
    prevJobId.current = selectedJobId;
    if (!onApplySingleGL) return;
    // Only apply if we have a customer or job to set
    if (!selectedCustomerId && !selectedJobId) return;
    onApplySingleGL({
      account_code: selectedAccountCode,
      allocation_type: 'job',
      suggested_customer_id: selectedCustomerId || undefined,
      suggested_customer_name: selectedCustomerName || undefined,
      suggested_job_id: selectedJobId || undefined,
      suggested_job_name: selectedJobName || undefined,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, selectedJobId, selectedAccountCode]);

  // Auto-apply inventory item link when it changes in single mode.
  // Ensures the gate sees the linked_inventory_item_id without a manual click.
  const prevInventoryItemId = React.useRef('');
  React.useEffect(() => {
    if (!selectedAccountCode) return;
    if (selectedInventoryItemId === prevInventoryItemId.current) return;
    prevInventoryItemId.current = selectedInventoryItemId;
    if (!onApplySingleGL) return;
    if (!selectedInventoryItemId) return;
    onApplySingleGL({
      account_code: selectedAccountCode,
      allocation_type: 'stock',
      inventory_item_match: {
        item_id: selectedInventoryItemId,
        item_title: selectedInventoryItemName,
        sku: inventoryItemsCache.find(i => i.id === selectedInventoryItemId)?.sku || '',
      } as InventoryItemMatch,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventoryItemId, selectedAccountCode]);

  // Check if the document already has vehicle linkage from SmartTask capture.
  // Vehicle-linked expenses (fuel, toll, maintenance) carry vehicle_id and
  // vehicle_registration in the document's custom_fields and on the work_item
  // columns. When the GL permits vehicle allocation, pre-select that vehicle.
  const docCustomFields = (doc?.custom_fields as Record<string, unknown> | undefined) || {};
  const docVehicleId = (docCustomFields.vehicle_id as string) || (doc?.vehicle_id as string) || null;
  const docVehicleReg = (docCustomFields.vehicle_registration as string) || (doc?.vehicle_registration as string) || null;
  const hasExistingVehicleLink = Boolean(docVehicleId || docVehicleReg);

  // AI suggestion — use the first line as representative (all lines get the same GL in single mode)
  // Prefer the whole-document suggestion (single-GL mode); fall back to the
  // first per-line entry when per-line categorization was requested.
  const aiSuggestion = aiDocSuggestion ?? aiPerLineCategorization[0] ?? null;
  const hasAISuggestion = aiSuggestion && aiSuggestion.account_code;
  const allocLabel = aiSuggestion?.allocation_type
    ? aiSuggestion.allocation_type.charAt(0).toUpperCase() + aiSuggestion.allocation_type.slice(1)
    : null;
  const hasJobLink = aiSuggestion?.allocation_type === 'job'
    && (aiSuggestion.suggested_customer_name || aiSuggestion.suggested_job_name);
  const hasStockLink = aiSuggestion?.allocation_type === 'stock'
    && aiSuggestion.inventory_item_match;
  const hasAIFullChain = Boolean(hasAISuggestion && allocLabel && (
    aiSuggestion.allocation_type === 'overhead' ||
    aiSuggestion.allocation_type === 'suspense' ||
    aiSuggestion.allocation_type === 'vehicle' ||
    hasJobLink || hasStockLink
  ));

  // Check if the AI suggestion has already been applied to all line items
  const aiAppliedToAll = hasAISuggestion && allSameGL && currentGLCode === aiSuggestion!.account_code
    && (!aiSuggestion!.suggested_customer_id || productiveItems.every(it =>
      (it as DocumentLineItem & { linked_customer_id?: string }).linked_customer_id === aiSuggestion!.suggested_customer_id))
    && (!aiSuggestion!.suggested_job_id || productiveItems.every(it =>
      it.linked_job_id === aiSuggestion!.suggested_job_id))
    && (!aiSuggestion!.inventory_item_match || productiveItems.every(it =>
      (it as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id === aiSuggestion!.inventory_item_match!.item_id))
    && (aiSuggestion!.allocation_type !== 'vehicle' || productiveItems.every(it =>
      Boolean((it as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id)));

  const handleApplyAI = () => {
    if (!hasAISuggestion || !onApplySingleGL) return;
    onApplySingleGL({
      account_code: aiSuggestion!.account_code,
      allocation_type: aiSuggestion!.allocation_type,
      inventory_item_match: aiSuggestion!.inventory_item_match,
      suggested_customer_id: aiSuggestion!.suggested_customer_id,
      suggested_customer_name: aiSuggestion!.suggested_customer_name,
      suggested_job_id: aiSuggestion!.suggested_job_id,
      suggested_job_name: aiSuggestion!.suggested_job_name,
      suggested_vehicle_id: (aiSuggestion as { suggested_vehicle_id?: string | null }).suggested_vehicle_id ?? docVehicleId,
      suggested_vehicle_name: (aiSuggestion as { suggested_vehicle_name?: string | null }).suggested_vehicle_name ?? docVehicleReg,
    });
    setSelectedAccountCode(aiSuggestion!.account_code);
  };

  // Derive allocation type from the selected GL account. When the document is
  // vehicle-linked and the account allows 'vehicle', prefer it over the
  // subtype default ('overhead') so the captured vehicle isn't dropped.
  const derivedAllocType = selectedAccountCode
    ? getPreferredAllocationType(expenseAccounts, selectedAccountCode, hasExistingVehicleLink)
    : undefined;
  const needsJobLink = derivedAllocType === 'job';
  const needsStockLink = derivedAllocType === 'stock';
  const needsVehicleLink = derivedAllocType === 'vehicle';

  // Check if line items already have job allocation from SmartTask capture.
  // The technician may have linked the expense to a job/customer during capture,
  // but no GL account has been assigned yet. We still need to show the
  // customer/job selectors so the admin can see and override the original
  // allocation without having to apply AI suggestions first.
  const existingJobAllocation = productiveItems.find(it => {
    const at = (it as DocumentLineItem & { allocation_type?: string }).allocation_type;
    return at === 'job' && it.linked_job_id;
  });
  const hasExistingJobLink = Boolean(existingJobAllocation);
  const showJobSelectors = (needsJobLink && selectedAccountCode) || (hasExistingJobLink && !selectedAccountCode);

  // Check if line items already have stock allocation from SmartTask capture.
  const existingStockAllocation = productiveItems.find(it => {
    const at = (it as DocumentLineItem & { allocation_type?: string }).allocation_type;
    return at === 'stock' && (it as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id;
  });
  const hasExistingStockLink = Boolean(existingStockAllocation);
  const showStockSelectors = (needsStockLink && selectedAccountCode) || (hasExistingStockLink && !selectedAccountCode);

  const showVehicleSelector = (needsVehicleLink && selectedAccountCode) || (hasExistingVehicleLink && !selectedAccountCode);

  const handleApplyManual = () => {
    if (!selectedAccountCode || !onApplySingleGL) return;
    // If the selected GL matches the AI suggestion, use the full chain
    if (hasAISuggestion && aiSuggestion!.account_code === selectedAccountCode && hasAIFullChain) {
      handleApplyAI();
    } else if (needsJobLink && selectedCustomerId) {
      // Include customer/job links for job allocation
      onApplySingleGL({
        account_code: selectedAccountCode,
        allocation_type: 'job',
        suggested_customer_id: selectedCustomerId,
        suggested_customer_name: selectedCustomerName,
        suggested_job_id: selectedJobId || undefined,
        suggested_job_name: selectedJobName || undefined,
      });
    } else if (needsStockLink && selectedInventoryItemId) {
      // Include inventory item link for stock allocation
      onApplySingleGL({
        account_code: selectedAccountCode,
        allocation_type: 'stock',
        inventory_item_match: {
          item_id: selectedInventoryItemId,
          item_title: selectedInventoryItemName,
          sku: inventoryItemsCache.find(i => i.id === selectedInventoryItemId)?.sku || '',
        } as InventoryItemMatch,
      });
    } else if (needsVehicleLink) {
      // Include vehicle link for vehicle allocation. Use the document's
      // captured vehicle (from SmartTask) as the pre-selected value.
      onApplySingleGL({
        account_code: selectedAccountCode,
        allocation_type: 'vehicle',
        suggested_vehicle_id: selectedVehicleId || docVehicleId || undefined,
        suggested_vehicle_name: selectedVehicleName || docVehicleReg || undefined,
      });
    } else {
      onApplySingleGL({ account_code: selectedAccountCode });
    }
  };

  return (
    <div data-tour-id="expense-single-gl-bar" className={`border-t px-3 py-2 space-y-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      {/* Row 1: GL dropdown + manual apply */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>GL Account:</label>
        <div className="min-w-[220px] flex-1 max-w-[400px]">
          <SearchableAccountSelect
            accounts={expenseAccounts}
            value={selectedAccountCode}
            onChange={setSelectedAccountCode}
            disabled={false}
            isDark={isDarkMode}
            placeholder="- Select GL account -"
            recentKey={`expense-bulk-${productiveItems.length}`}
            className="text-xs"
          />
        </div>

        {/* Apply button (manual selection) */}
        <button
          type="button"
          onClick={handleApplyManual}
          disabled={!selectedAccountCode}
          className={`ml-auto flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            isDarkMode ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          }`}
          title="Apply this GL allocation to all line items"
        >
          <Check className="h-3 w-3" />
          Apply to all {productiveItems.length} items
        </button>
      </div>

      {/* Row 1b: Customer + Job selectors — when GL allocation type is 'job' OR
          when line items already have job allocation from SmartTask capture.
          In single mode the user otherwise has no way to see/set the customer/job
          link, which the final action gate requires. Showing it even without a
          GL account selected makes the technician's original allocation visible. */}
      {showJobSelectors && (
        <div className="flex items-center gap-2 flex-wrap pl-1">
          <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Customer:</label>
          <div className="min-w-[180px] flex-1 max-w-[260px]">
            <AsyncSearchableSelect
              value={selectedCustomerId}
              onChange={(val) => {
                setSelectedCustomerId(val);
                const matched = customersCache.find((c) => c.id === val);
                setSelectedCustomerName(matched?.company_name || matched?.contact_person || '');
                // Clear job when customer changes
                setSelectedJobId('');
                setSelectedJobName('');
              }}
              onSearch={async (term) => {
                const token = authService.getTokens()?.access_token;
                if (!token) return [];
                const params = new URLSearchParams({ is_active: 'true', limit: '50', contact_types: 'customer' });
                if (term.trim()) params.set('search', term);
                const res = await fetch(`/api/v1/contacts?${params}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return [];
                const data = await res.json();
                const contacts = (data.data || []) as Array<Record<string, unknown>>;
                setCustomersCache(contacts.map((c) => ({
                  id: String(c.id || ''),
                  company_name: (c.company_name as string | null) ?? null,
                  contact_person: (c.contact_person as string | null) ?? null,
                })));
                return contacts.map((c: Record<string, unknown>) => ({
                  value: String(c.id || ''),
                  label: String(c.company_name || c.contact_person || `Contact ${c.id}`),
                  icon: <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
                }));
              }}
              isDark={isDarkMode}
              placeholder="Search customer..."
              searchPlaceholder="Search customers..."
              emptyMessage="No customers found"
              idleMessage="Start typing to search customers..."
              idPrefix="single-cust"
              loadOnOpen
              selectedDisplay={selectedCustomerName || undefined}
              selectedIcon={<Building2 className="w-4 h-4 shrink-0 text-blue-600" />}
              className="!px-2 !py-1.5"
            />
          </div>
          {selectedCustomerId && (
            <>
              <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Job:</label>
              <div className="min-w-[180px] flex-1 max-w-[260px]">
                <AsyncSearchableSelect
                  value={selectedJobId}
                  onChange={(val) => {
                    setSelectedJobId(val);
                    const matched = jobsCache.find((j) => j.id === val);
                    setSelectedJobName(matched ? `${matched.item_number} — ${matched.title}` : '');
                  }}
                  onSearch={async (term) => {
                    const token = authService.getTokens()?.access_token;
                    if (!token) return [];
                    const params = new URLSearchParams({
                      item_type: 'job',
                      status: 'active,in_progress,open,pending',
                      limit: '200',
                      contact_id: selectedCustomerId,
                    });
                    if (term.trim()) params.set('search', term);
                    const res = await fetch(`/api/v1/work-items?${params}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) return [];
                    const data = await res.json();
                    const jobs = (data.data || []) as Array<Record<string, unknown>>;
                    setJobsCache(jobs.map((j) => ({
                      id: String(j.id || ''),
                      item_number: String(j.item_number || ''),
                      title: String(j.title || ''),
                      status: String(j.status || 'active'),
                    })));
                    return jobs.map((j: Record<string, unknown>) => ({
                      value: String(j.id || ''),
                      label: `${j.item_number || ''} — ${j.title || ''}`,
                      icon: <Briefcase className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
                    }));
                  }}
                  isDark={isDarkMode}
                  placeholder="Search job..."
                  searchPlaceholder="Search jobs..."
                  emptyMessage="No jobs found for this customer"
                  idleMessage="Start typing to search jobs..."
                  idPrefix="single-job"
                  loadOnOpen
                  selectedDisplay={selectedJobName || undefined}
                  selectedIcon={<Briefcase className="w-4 h-4 shrink-0 text-green-600" />}
                  className="!px-2 !py-1.5"
                />
              </div>
            </>
          )}
          {/* Re-apply with links button */}
          {selectedCustomerId && (
            <button
              type="button"
              onClick={handleApplyManual}
              disabled={!selectedAccountCode}
              className={`ml-auto flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                isDarkMode ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              }`}
              title="Apply GL + customer/job allocation to all line items"
            >
              <Check className="h-3 w-3" />
              Apply with links
            </button>
          )}
        </div>
      )}

      {/* Row 1c: Inventory item selector — when GL allocation type is 'stock'
          OR when line items already have stock allocation from SmartTask capture.
          Without this, the user can select a stock GL account but has no way to
          link an inventory item, which the completion gate requires. */}
      {showStockSelectors && (
        <div className="flex items-center gap-2 flex-wrap pl-1">
          <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Inventory Item:</label>
          <div className="min-w-[220px] flex-1 max-w-[400px]">
            <AsyncSearchableSelect
              value={selectedInventoryItemId}
              onChange={(val) => {
                setSelectedInventoryItemId(val);
                const matched = inventoryItemsCache.find((i) => i.id === val);
                setSelectedInventoryItemName(matched?.title || '');
              }}
              onSearch={async (term) => {
                const token = authService.getTokens()?.access_token;
                if (!token) return [];
                const params = new URLSearchParams({ item_type: 'inventory', page: '1', limit: '50' });
                if (term.trim()) params.set('search', term);
                const res = await fetch(`/api/v1/work-items?${params}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return [];
                const data = await res.json();
                const items = (data.data || []) as Array<Record<string, unknown>>;
                setInventoryItemsCache(items.map((item) => ({
                  id: String(item.id || ''),
                  title: String(item.title || item.name || ''),
                  sku: (item.sku as string | null) ?? null,
                  item_number: (item.item_number as string | null) ?? null,
                })));
                return items.map((item: Record<string, unknown>) => ({
                  value: String(item.id || ''),
                  label: String(item.title || item.name || ''),
                  description: [item.sku, item.item_number].filter(Boolean).map(String).join(' · ') || undefined,
                  icon: <Package className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
                }));
              }}
              isDark={isDarkMode}
              placeholder="Search inventory item..."
              searchPlaceholder="Search inventory..."
              emptyMessage="No inventory items found"
              idleMessage="Start typing to search inventory..."
              idPrefix="single-inv"
              loadOnOpen
              selectedDisplay={selectedInventoryItemName || undefined}
              selectedIcon={<Package className="w-4 h-4 shrink-0 text-blue-600" />}
              className="!px-2 !py-1.5"
            />
          </div>
          {selectedInventoryItemId && (
            <button
              type="button"
              onClick={handleApplyManual}
              disabled={!selectedAccountCode}
              className={`ml-auto flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                isDarkMode ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              }`}
              title="Apply GL + inventory allocation to all line items"
            >
              <Check className="h-3 w-3" />
              Apply with link
            </button>
          )}
        </div>
      )}

      {/* Row 1d: Vehicle selector — when GL allocation type is 'vehicle'
          OR when the document already has vehicle linkage from SmartTask capture.
          Pre-selects the vehicle that was captured at intake. */}
      {showVehicleSelector && (
        <div className="flex items-center gap-2 flex-wrap pl-1">
          <label className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vehicle:</label>
          <div className="min-w-[220px] flex-1 max-w-[400px]">
            <AsyncSearchableSelect
              value={selectedVehicleId}
              onChange={(val) => {
                setSelectedVehicleId(val);
                const matched = vehiclesCache.find((v) => v.id === val);
                setSelectedVehicleName(matched?.title || matched?.registration || '');
              }}
              onSearch={async (term) => {
                const token = authService.getTokens()?.access_token;
                if (!token) return [];
                const params = new URLSearchParams({ item_type: 'asset', item_subtype: 'vehicle', page: '1', limit: '50' });
                if (term.trim()) params.set('search', term);
                const res = await fetch(`/api/v1/work-items?${params}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return [];
                const data = await res.json();
                const items = (data.data || []) as Array<Record<string, unknown>>;
                setVehiclesCache(items.map((item) => ({
                  id: String(item.id || ''),
                  title: String(item.title || item.name || ''),
                  registration: (item.registration as string | null) ?? null,
                })));
                return items.map((item: Record<string, unknown>) => ({
                  value: String(item.id || ''),
                  label: String(item.title || item.name || item.registration || ''),
                  description: [item.registration, item.item_number].filter(Boolean).map(String).join(' · ') || undefined,
                  icon: <Truck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />,
                }));
              }}
              isDark={isDarkMode}
              placeholder="Search vehicle..."
              searchPlaceholder="Search vehicles..."
              emptyMessage="No vehicles found"
              idleMessage="Start typing to search vehicles..."
              idPrefix="single-veh"
              loadOnOpen
              selectedDisplay={selectedVehicleName || undefined}
              selectedIcon={<Truck className="w-4 h-4 shrink-0 text-blue-600" />}
              className="!px-2 !py-1.5"
            />
          </div>
          {selectedVehicleId && (
            <button
              type="button"
              onClick={handleApplyManual}
              disabled={!selectedAccountCode}
              className={`ml-auto flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                isDarkMode ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              }`}
              title="Apply GL + vehicle allocation to all line items"
            >
              <Check className="h-3 w-3" />
              Apply with link
            </button>
          )}
        </div>
      )}

      {/* Row 2: AI suggestion with Apply All (document-level) */}
      {aiSuggestionsLoading && !hasAISuggestion && (
        <div className={`flex items-center gap-1.5 rounded px-2 py-1.5 ${isDarkMode ? 'bg-purple-950/40 border border-purple-800/50' : 'bg-purple-50 border border-purple-100'}`}>
          <Sparkles className="h-3.5 w-3.5 text-purple-500 animate-pulse flex-shrink-0" />
          <span className={`text-[11px] ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>AI categorizing all line items...</span>
        </div>
      )}
      {hasAISuggestion && (() => {
        const suggestion = aiSuggestion!;
        return (
          <div className={`flex items-center gap-1.5 flex-wrap rounded px-2 py-1.5 ${isDarkMode ? 'bg-purple-950/40 border border-purple-800/50' : 'bg-purple-50 border border-purple-100'}`}>
            <Sparkles className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
            <span className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              AI suggests for all items:
            </span>
            <span className={`text-[11px] truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {suggestion.account_name}
            </span>
            <span className={`text-[11px] font-mono shrink-0 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>
              {suggestion.account_code}
            </span>
            {(suggestion.reasons?.length ?? 0) > 0 && (
              <span title={suggestion.reasons.join(' • ')} className="inline-flex">
                <HelpCircle className={`h-3 w-3 cursor-help ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`} />
              </span>
            )}
            <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${suggestion.confidence >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : suggestion.confidence >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
              {Math.round(suggestion.confidence)}%
            </span>

            {/* Apply All — full chain */}
            {onApplySingleGL && !aiAppliedToAll && (
              <button
                type="button"
                onClick={handleApplyAI}
                className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${isDarkMode ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                title={`Apply ${suggestion.account_code}${allocLabel ? ` → ${allocLabel}` : ''}${hasJobLink ? ` → ${suggestion.suggested_customer_name || ''}${suggestion.suggested_job_name ? ` → ${suggestion.suggested_job_name}` : ''}` : ''}${hasStockLink ? ` → ${suggestion.inventory_item_match?.item_title}` : ''} to all line items`}
              >
                <Check className="h-3 w-3" />
                Apply All
              </button>
            )}
            {aiAppliedToAll && (
              <span className={`text-[10px] font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                ✓ Applied to all items
              </span>
            )}
          </div>
        );
      })()}

      {/* Row 3: Full chain summary (allocation type + links) */}
      {hasAISuggestion && aiSuggestion?.allocation_type && (() => {
        const suggestion = aiSuggestion!;
        const label = suggestion.allocation_type!.charAt(0).toUpperCase() + suggestion.allocation_type!.slice(1);
        const jobLink = suggestion.allocation_type === 'job' && (suggestion.suggested_customer_name || suggestion.suggested_job_name);
        const stockLink = suggestion.allocation_type === 'stock' && suggestion.inventory_item_match;
        return (
          <div className={`flex items-center gap-1 flex-wrap px-2 text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span className={`px-1 py-0.5 rounded font-medium ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}>
              {label}
            </span>
            {jobLink && (
              <>
                {suggestion.suggested_customer_name && (
                  <span className="flex items-center gap-0.5">
                    <span className="opacity-50">→</span>
                    {suggestion.suggested_customer_name}
                  </span>
                )}
                {suggestion.suggested_job_name && (
                  <span className="flex items-center gap-0.5">
                    <span className="opacity-50">→</span>
                    {suggestion.suggested_job_name}
                  </span>
                )}
              </>
            )}
            {stockLink && suggestion.inventory_item_match && (
              <span className="flex items-center gap-0.5">
                <span className="opacity-50">→</span>
                {suggestion.inventory_item_match.item_title}
                {suggestion.inventory_item_match.sku && (
                  <span className="font-mono opacity-60">({suggestion.inventory_item_match.sku})</span>
                )}
              </span>
            )}
            {(suggestion.allocation_type === 'overhead' || suggestion.allocation_type === 'suspense') && (
              <span className="opacity-60">— no link required</span>
            )}
          </div>
        );
      })()}

      {/* Row 3b: Current line item allocation summary — shows the actual allocation
          from loaded line items (e.g. job-linked expense from SmartTask capture).
          This makes the original submission context visible to the admin so they
          can see and override it, even when AI suggestions are present. The AI
          suggestion (Row 2/3 above) may suggest a different allocation, but the
          reviewer should always know what the technician originally selected. */}
      {(() => {
        const firstWithAlloc = productiveItems.find(it => {
          const at = (it as DocumentLineItem & { allocation_type?: string }).allocation_type;
          return at && at !== 'overhead';
        });
        const firstOverhead = productiveItems.find(it => {
          const at = (it as DocumentLineItem & { allocation_type?: string }).allocation_type;
          return at === 'overhead';
        });
        if (!firstWithAlloc && !firstOverhead) return null;
        const representative = firstWithAlloc || firstOverhead;
        const allocType = ((representative as DocumentLineItem & { allocation_type?: string }).allocation_type || 'overhead') as string;
        const label = allocType.charAt(0).toUpperCase() + allocType.slice(1);
        const jobName = (representative as DocumentLineItem & { linked_job_name?: string }).linked_job_name;
        const customerName = (representative as DocumentLineItem & { linked_customer_name?: string }).linked_customer_name;
        const inventoryName = (representative as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name;
        const isJobLinked = allocType === 'job' && (jobName || customerName);
        const isStockLinked = allocType === 'stock' && inventoryName;
        const allSameAlloc = productiveItems.every(it => {
          const at = (it as DocumentLineItem & { allocation_type?: string }).allocation_type || 'overhead';
          return at === allocType;
        });
        return (
          <div className={`flex items-center gap-1 flex-wrap px-2 text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span className="opacity-60">Submitted as:</span>
            <span className={`px-1 py-0.5 rounded font-medium ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}>
              {label}
            </span>
            {isJobLinked && (
              <>
                {customerName && (
                  <span className="flex items-center gap-0.5">
                    <span className="opacity-50">→</span>
                    {customerName}
                  </span>
                )}
                {jobName && (
                  <span className="flex items-center gap-0.5">
                    <span className="opacity-50">→</span>
                    {jobName}
                  </span>
                )}
              </>
            )}
            {isStockLinked && (
              <span className="flex items-center gap-0.5">
                <span className="opacity-50">→</span>
                {inventoryName}
              </span>
            )}
            {allocType === 'overhead' && (
              <span className="opacity-60">— general expense (no job link)</span>
            )}
            {!allSameAlloc && (
              <span className={`ml-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                (mixed allocations — switch to Per-line GL to edit individually)
              </span>
            )}
          </div>
        );
      })()}

      {/* Current state indicator */}
      {allSameGL && currentGLCode && !aiAppliedToAll && (
        <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          All line items currently allocated to: {currentGLAccount?.name || currentGLCode} ({currentGLCode})
        </div>
      )}
      {!allSameGL && productiveItems.length > 0 && (
        <div className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Line items have mixed GL allocations — selecting above and clicking Apply will unify them.
        </div>
      )}
    </div>
  );
}
